import React, { useState } from 'react';
import { apiService } from '../../services/api';
import { useMutation } from '../../hooks/useApi';
import logger from '../../utils/logger';
import { FileDown, Eye, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

function RaportHarxhime() {
  const { roli } = useAuth();
  const { getCurrencyCode } = useSettings();
  const currencyCode = getCurrencyCode();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [harxhimet, setHarxhimet] = useState({});
  const [error, setError] = useState('');
  const [faqjaAktuale, setFaqjaAktuale] = useState(1);
  const perFaqe = 5;

  const { mutate: fetchRaport, loading: loadingRaport } = useMutation(
    async ({ from, to }) => {
      return await apiService.get('/raport-harxhime', { params: { from, to } });
    },
    {
      onSuccess: (data) => {
        setHarxhimet(data);
        setFaqjaAktuale(1);
        setError('');
      },
      onError: () => {
        setError('Nuk mund të merret raporti i harxhimeve.');
      },
      showNotifications: false
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFaqjaAktuale(1);
    fetchRaport({ from, to });
  };

  const handleExportPdf = async () => {
    if (!from || !to) {
      setError('Ju lutem zgjidhni datat për PDF.');
      return;
    }
    try {
      const response = await apiService.get('/raport-harxhime/pdf', {
        params: { from, to },
        responseType: 'blob'
      });
      const file = new Blob([response], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (err) {
      logger.error('PDF export error:', err);
      setError('Dështoi shkarkimi i PDF-it.');
    }
  };

  const renderTabela = (lista, lokacioni) => {
    const offset = (faqjaAktuale - 1) * perFaqe;
    const faqja = lista.slice(offset, offset + perFaqe);
    const faqetTotale = Math.ceil(lista.length / perFaqe);
    const totali = lista.reduce((sum, h) => sum + (Number(h.shuma) || 0), 0);

    return (
      <div key={lokacioni} className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Lokacioni: {lokacioni}</h3>

        {/* Pagination */}
        <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
          <span>Faqe {faqjaAktuale} nga {faqetTotale}</span>
          <div className="flex gap-1">
            <button onClick={() => setFaqjaAktuale(1)} disabled={faqjaAktuale === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale - 1)} disabled={faqjaAktuale === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale + 1)} disabled={faqjaAktuale === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
            <button onClick={() => setFaqjaAktuale(faqetTotale)} disabled={faqjaAktuale === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight size={16} /></button>
          </div>
        </div>

        {/* Tabela desktop */}
        <div className="hidden md:block">
          <div className="card-premium overflow-hidden p-0">
            <table className="table-premium">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Lloji</th>
                  <th className="text-right">Shuma</th>
                </tr>
              </thead>
              <tbody>
                {faqja.map((h, i) => (
                  <tr key={i}>
                    <td>{h.data}</td>
                    <td>{h.emri}</td>
                    <td className="text-right font-semibold text-red-600 dark:text-red-400">{Number(h.shuma).toFixed(2)} {currencyCode}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 dark:bg-dark-700 font-bold">
                  <td className="text-right" colSpan="2">Totali:</td>
                  <td className="text-right text-red-600 dark:text-red-400">{totali.toFixed(2)} {currencyCode}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Kartela mobile */}
        <div className="md:hidden space-y-4">
          {faqja.map((h, i) => (
            <div key={i} className="card-premium p-4 space-y-1">
              <div><strong className="text-gray-700 dark:text-gray-300">Lloji:</strong> {h.emri}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Data:</strong> {h.data}</div>
              <div><strong className="text-red-700 dark:text-red-400">Shuma:</strong> <span className="font-semibold">{Number(h.shuma).toFixed(2)} {currencyCode}</span></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">💸 Raporti i Harxhimeve</h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="form-label">Nga data</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="input-premium"
            required
          />
        </div>
        <div>
          <label className="form-label">Deri më</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="input-premium"
            required
          />
        </div>
        <button
          type="submit"
          className="btn-premium bg-gradient-to-r from-red-500 to-red-600 mt-auto flex items-center justify-center gap-2"
        >
          <Eye size={18} /> Shfaq
        </button>
      </form>

      {error && <p className="p-3 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 font-medium">{error}</p>}

      {Object.keys(harxhimet).length > 0 && (
        <div className="space-y-10">
          <button
            onClick={handleExportPdf}
            className="btn-premium flex items-center gap-2"
          >
            <FileDown size={18} /> Shkarko PDF
          </button>

          {Object.entries(harxhimet).map(([lokacioni, lista]) =>
            renderTabela(lista, lokacioni)
          )}
        </div>
      )}
    </div>
  );
}

export default RaportHarxhime;
